import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Factory, Settings, Palette } from 'lucide-react';
import SEO from '../components/SEO';
import './ServicioSEO.css';

const Impresion3D = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Impresión 3D",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Artelaser3D",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Asturias",
        "addressRegion": "Asturias",
        "addressCountry": "ES"
      }
    },
    "areaServed": [
      { "@type": "City", "name": "Oviedo" },
      { "@type": "City", "name": "Gijón" },
      { "@type": "City", "name": "Avilés" },
      { "@type": "Country", "name": "España" }
    ],
    "description": "Servicio profesional de impresión 3D en Asturias. Prototipos, piezas técnicas, decoración y producción en serie. Tecnología FDM de alta calidad.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock"
    }
  };

  const technologies = [
    {
      name: 'FDM (Filamento)',
      description: 'Tecnología de deposición de material fundido. Ideal para piezas resistentes, funcionales y prototipos estructurales.',
      materials: ['PLA', 'PETG', 'ABS', 'TPU', 'Nylon', 'Fibra de carbono'],
      applications: ['Prototipos funcionales', 'Piezas mecánicas', 'Herramientas', 'Juguetes']
    }
  ];

  const services = [
    {
      title: 'Prototipado Rápido',
      description: 'Valida tus diseños antes de la inversión en moldes o producción masiva. Iteraciones rápidas y económicas.',
      icon: <Zap size={48} />
    },
    {
      title: 'Producción en Serie',
      description: 'Fabricación de 10 a 500 unidades con calidad consistente. Perfecto para lanzamientos de producto o reposición.',
      icon: <Factory size={48} />
    },
    {
      title: 'Piezas Técnicas',
      description: 'Componentes funcionales con tolerancias precisas. Engranajes, soportes, carcasas y repuestos.',
      icon: <Settings size={48} />
    },
    {
      title: 'Decoración 3D',
      description: 'Lámparas, jarrones, esculturas y elementos decorativos únicos para hogares y negocios.',
      icon: <Palette size={48} />
    }
  ];

  const faqs = [
    {
      question: '¿Puedo imprimir cualquier diseño 3D?',
      answer: 'La mayoría de diseños 3D son imprimibles, pero deben estar correctamente preparados. Aceptamos archivos STL, OBJ y 3MF. Si tienes un modelo que necesita ajustes, también ofrecemos servicio de preparación de archivos.'
    },
    {
      question: '¿Cuánto tarda una impresión 3D?',
      answer: 'Depende del tamaño y complejidad. Una pieza pequeña puede tardar 2-4 horas, mientras que proyectos grandes pueden necesitar 20+ horas. Siempre te damos un tiempo estimado antes de comenzar.'
    },
    {
      question: '¿Es resistente el material impreso?',
      answer: 'Depende del material. PLA es rígido pero menos resistente al calor. PETG y ABS ofrecen mayor resistencia mecánica y térmica. Nylon y fibra de carbono son los más resistentes para aplicaciones exigentes.'
    }
  ];

  return (
    <>
      <SEO 
        title="Impresión 3D en Asturias"
        description="Servicio profesional de impresión 3D en Asturias. Prototipos, piezas técnicas, decoración y producción en serie. Tecnología FDM de alta calidad. Envíos a toda España."
        keywords="impresión 3D Asturias, impresión 3D Oviedo, impresión 3D Gijón, prototipado 3D, servicios 3D Asturias, impresión FDM"
        canonicalUrl="https://artelaser3d.es/impresion-3d-asturias"
        schema={schema}
      />

      <section className="page-hero page-hero--3d">
        <div className="page-hero__container">
          <span className="page-hero__badge">Impresión 3D</span>
          <h1 className="page-hero__title">
            Impresión 3D de
            <span className="text-primary"> alta calidad</span> en Asturias
          </h1>
          <p className="page-hero__description">
            Prototipos funcionales, piezas técnicas y objetos personalizados. 
            Tecnología FDM de alta calidad para cada necesidad. Servicio profesional 
            para particulares y empresas.
          </p>
          <div className="page-hero__cta">
            <Link to="/presupuesto" className="btn btn--white btn--lg">
              Solicitar Presupuesto
            </Link>
          </div>
        </div>
      </section>

      <section className="section section--technologies section--visible">
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">Tecnología</span>
            <h2>Tecnología FDM de alta calidad</h2>
            <p className="section__description">
              Utilizamos tecnología FDM (Filamento) para ofrecerte piezas 
              resistentes y funcionales adaptadas a tus necesidades.
            </p>
          </header>

          <div className="technologies-grid">
            {technologies.map((tech, index) => (
              <article key={index} className="technology-card">
                <h3>{tech.name}</h3>
                <p>{tech.description}</p>
                
                <div className="technology-card__section">
                  <strong>Materiales:</strong>
                  <ul>
                    {tech.materials.map((mat, i) => (
                      <li key={i}>{mat}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="technology-card__section">
                  <strong>Aplicaciones:</strong>
                  <ul>
                    {tech.applications.map((app, i) => (
                      <li key={i}>{app}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--services-3d section--visible">
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">Servicios</span>
            <h2>¿Qué podemos imprimir para ti?</h2>
          </header>

          <div className="services-3d-grid">
            {services.map((service, index) => (
              <article key={index} className="service-3d-card">
                <span className="service-3d-card__icon">{service.icon}</span>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--workflow section--visible">
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">Proceso</span>
            <h2>De tu idea a la pieza final</h2>
          </header>

          <div className="workflow-steps">
            <div className="workflow-step">
              <span className="workflow-step__number">1</span>
              <h3>Modelado 3D</h3>
              <p>Creamos o adaptamos tu diseño en formato 3D listo para imprimir.</p>
            </div>

            <div className="workflow-step">
              <span className="workflow-step__number">2</span>
              <h3>Configuración</h3>
              <p>Ajustamos parámetros de impresión para óptima calidad y resistencia.</p>
            </div>

            <div className="workflow-step">
              <span className="workflow-step__number">3</span>
              <h3>Impresión</h3>
              <p>Fabricación con monitoreo constante para garantizar calidad.</p>
            </div>

            <div className="workflow-step">
              <span className="workflow-step__number">4</span>
              <h3>Acabado</h3>
              <p>Limpieza, post-procesado y control de calidad final.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section section--faq section--visible">
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">FAQ</span>
            <h2>Preguntas frecuentes sobre impresión 3D</h2>
          </header>

          <div className="faq-list">
            {faqs.map((faq, index) => (
              <article key={index} className="faq-item">
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--cta section--cta--impresion section--visible">
        <div className="cta__content">
          <h2 className="cta__title--impresion">¿Tienes un proyecto de impresión 3D?</h2>
          <p className="cta__description--impresion">
            Solicita tu presupuesto sin compromiso. Respuesta en 24 horas.
          </p>
          <div className="cta__buttons">
            <Link to="/presupuesto" className="btn btn--primary btn--lg">
              Solicitar Presupuesto Gratis
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Impresion3D;
